"use client";

import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function HeroSection() {
  const { t } = useLanguage();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const [mounted, setMounted] = useState(false);
  const heroRef = useRef(null);
  const inView = useInView(heroRef, { once: true, amount: 0.2 });

  // Parallax scroll transforms
  const { scrollY } = useScroll();
  const blobTopY = useTransform(scrollY, [0, 600], [0, -120]);
  const blobBottomY = useTransform(scrollY, [0, 600], [0, -60]);
  const canvasY = useTransform(scrollY, [0, 600], [0, -80]);
  const card1Y = useTransform(scrollY, [0, 600], [0, -100]);
  const card2Y = useTransform(scrollY, [0, 600], [0, -70]);
  const card3Y = useTransform(scrollY, [0, 600], [0, -90]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = {
        x: (e.clientX / window.innerWidth - 0.5) * 2,
        y: (e.clientY / window.innerHeight - 0.5) * 2,
      };
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    const particles: Array<{
      x: number; y: number; vx: number; vy: number;
      radius: number; opacity: number; color: string;
    }> = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Create particles
    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        radius: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.5 + 0.1,
        color: Math.random() > 0.7 ? '#FF912E' : '#8B9EC0',
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 140) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(139, 158, 192, ${(1 - dist / 140) * 0.12})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      // Draw + update particles
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.color === '#FF912E'
          ? `rgba(255, 145, 46, ${p.opacity})`
          : `rgba(139, 158, 192, ${p.opacity})`;
        ctx.fill();
      });

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  // Floating elements parallax
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let rafId: number;

    const update = () => {
      const floaters = container.querySelectorAll<HTMLElement>('[data-depth]');
      floaters.forEach((el) => {
        const depth = parseFloat(el.dataset.depth || '0.5');
        const x = mouseRef.current.x * depth * 20;
        const y = mouseRef.current.y * depth * 15;
        el.style.transform = `translate(${x}px, ${y}px)`;
      });
      rafId = requestAnimationFrame(update);
    };
    rafId = requestAnimationFrame(update);
    return () => cancelAnimationFrame(rafId);
  }, [mounted]);

  return (
    <section
      ref={containerRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden hero-gradient"
      style={{ paddingTop: '96px' }}
    >
      {/* Particle canvas — slowest parallax layer */}
      <motion.canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        style={{ zIndex: 1, y: canvasY }}
      />

      {/* Blob layers — mid-speed parallax */}
      <motion.div
        className="absolute top-[10%] left-[5%] w-[500px] h-[500px] blob opacity-20 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(30, 58, 95, 0.8) 0%, transparent 70%)',
          zIndex: 1,
          animationDelay: '0s',
          y: blobTopY,
        }}
        data-depth="0.3"
      />
      <motion.div
        className="absolute bottom-[10%] right-[5%] w-[400px] h-[400px] blob opacity-15 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(255, 145, 46, 0.15) 0%, transparent 70%)',
          zIndex: 1,
          animationDelay: '-4s',
          y: blobBottomY,
        }}
        data-depth="0.2"
      />

      {/* Floating UI card 1 — top right — fastest parallax */}
      <motion.div
        className="absolute top-[18%] right-[8%] hidden xl:block animate-float card-glass rounded-2xl p-4 w-56 pointer-events-none"
        style={{ zIndex: 3, animationDelay: '0s', y: card1Y }}
        data-depth="0.6"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <svg width="16" height="16" fill="white" viewBox="0 0 24 24">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
            </svg>
          </div>
          <div>
            <div className="text-xs font-bold text-white">{t('hero_card1_title')}</div>
            <div className="text-[10px] text-muted">{t('hero_card1_sub')}</div>
          </div>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between text-[11px]">
            <span className="text-muted">{t('hero_card1_tasks')}</span>
            <span className="text-primary font-bold">247</span>
          </div>
          <div className="h-1.5 bg-[rgba(139,158,192,0.15)] rounded-full overflow-hidden">
            <div className="h-full w-[78%] bg-gradient-to-r from-primary to-orange-400 rounded-full" />
          </div>
          <div className="flex justify-between text-[10px] text-muted">
            <span>{t('hero_card1_efficiency')}</span>
            <span className="text-green-400 font-semibold">+78%</span>
          </div>
        </div>
      </motion.div>

      {/* Floating UI card 2 — left middle */}
      <motion.div
        className="absolute left-[6%] top-[40%] hidden xl:block animate-float card-glass rounded-2xl p-4 w-52 pointer-events-none"
        style={{ zIndex: 3, animationDelay: '-2.5s', y: card2Y }}
        data-depth="0.45"
      >
        <div className="text-[10px] text-muted uppercase tracking-widest mb-2">{t('hero_card2_label')}</div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse-glow" />
          <span className="text-sm font-semibold text-white">{t('hero_card2_title')}</span>
        </div>
        <div className="space-y-2">
          {[t('hero_card2_step1'), t('hero_card2_step2'), t('hero_card2_step3')].map((step, i) => (
            <div key={step} className="flex items-center gap-2">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${i < 2 ? 'bg-primary text-white' : 'bg-[rgba(139,158,192,0.2)] text-muted'}`}>
                {i < 2 ? '✓' : i + 1}
              </div>
              <span className={`text-[11px] ${i < 2 ? 'text-white' : 'text-muted'}`}>{step}</span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Floating UI card 3 — bottom right */}
      <motion.div
        className="absolute bottom-[22%] right-[10%] hidden xl:block animate-float card-glass rounded-2xl p-4 w-48 pointer-events-none"
        style={{ zIndex: 3, animationDelay: '-5s', y: card3Y }}
        data-depth="0.55"
      >
        <div className="text-[10px] text-muted mb-2">{t('hero_card3_label')}</div>
        <div className="text-3xl font-heading font-bold text-gradient-orange">32hrs</div>
        <div className="text-[11px] text-green-400 mt-1">{t('hero_card3_sub')}</div>
      </motion.div>

      {/* Main hero content */}
      <div ref={heroRef} className="relative z-10 max-w-container mx-auto px-6 text-center" style={{ zIndex: 4 }}>
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          custom={0}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[rgba(255,145,46,0.25)] bg-[rgba(255,145,46,0.06)] mb-8"
        >
          <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
          <span className="section-label">{t('hero_label')}</span>
        </motion.div>

        <motion.h1
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          custom={1}
          className="font-heading text-display font-bold text-white mb-6 max-w-4xl mx-auto"
        >
          {t('hero_headline_1')}{' '}
          <span className="text-gradient-orange">{t('hero_headline_accent')}</span>
          {' '}{t('hero_headline_2')}
        </motion.h1>

        <motion.p
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          custom={2}
          className="text-lg md:text-xl text-muted max-w-2xl mx-auto mb-10 leading-relaxed"
        >
          {t('hero_sub')}
        </motion.p>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          custom={3}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href="https://calendly.com/devros-free-consultation?utm_source=website"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary px-8 py-4 rounded-xl text-base font-bold w-full sm:w-auto text-center inline-flex items-center justify-center gap-2"
          >
            <span>{t('hero_cta_primary')}</span>
            <svg width="16" height="16" fill="none" stroke="white" strokeWidth="2.5" viewBox="0 0 24 24">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </a>
          <a
            href="#services"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#services')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="px-8 py-4 rounded-xl text-base font-semibold text-white border border-[rgba(139,158,192,0.25)] hover:border-[rgba(255,145,46,0.4)] hover:bg-[rgba(255,145,46,0.06)] transition-all duration-300 w-full sm:w-auto text-center"
          >
            {t('hero_cta_secondary')}
          </a>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          custom={4}
          className="mt-16 flex flex-col items-center gap-2"
        >
          <div className="w-px h-12 bg-gradient-to-b from-transparent via-[rgba(139,158,192,0.4)] to-transparent" />
          <div className="w-1.5 h-1.5 rounded-full bg-muted animate-bounce" />
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        style={{ zIndex: 4, animation: 'fadeInUp 1s 0.6s cubic-bezier(0.22, 1, 0.36, 1) both' }}
      >
        <span className="text-[11px] text-muted uppercase tracking-widest">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-muted/40 to-transparent" />
      </div>
    </section>
  );
}